
import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useClientesPaginated, useClienteById } from "@/hooks/useClientes";
import { Search, User } from "lucide-react";

interface ClienteSearchSelectProps {
  value: string;
  onChange: (clienteId: string, clienteNome: string) => void;
  label?: string;
  required?: boolean;
}

export function ClienteSearchSelect({
  value,
  onChange,
  label = "Selecionar Cliente",
  required,
}: ClienteSearchSelectProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const canQuery =
    debouncedSearch.length >= 2 || (/^\d+$/.test(debouncedSearch) && debouncedSearch.length > 0);

  const { data: clientesResult, isLoading, isFetching } = useClientesPaginated(
    1,
    30,
    canQuery ? debouncedSearch : undefined,
    null,
    null,
    { enabled: canQuery }
  );

  const clientes = clientesResult?.data ?? [];
  const clienteIdNum = /^\d+$/.test(value) ? parseInt(value, 10) : null;
  const { data: clientePorId } = useClienteById(clienteIdNum, !!clienteIdNum);

  useEffect(() => {
    if (!value) {
      if (!isOpen) setSearchTerm("");
      return;
    }
    const found = clientes.find((c) => c.id.toString() === value);
    if (found) {
      setSearchTerm(found.nome);
      return;
    }
    if (clientePorId?.nome && !isOpen) {
      setSearchTerm(clientePorId.nome);
    }
  }, [value, clientes, isOpen, clientePorId?.nome]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClienteSelect = (cliente: { id: number; nome: string }) => {
    onChange(cliente.id.toString(), cliente.nome);
    setSearchTerm(cliente.nome);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    if (newValue === "") {
      onChange("", "");
    } else if (value) {
      onChange("", "");
    }
  };

  const formatCpfCnpj = (cpfCnpj: string) => {
    if (!cpfCnpj) return "";
    if (cpfCnpj.length <= 11) {
      return cpfCnpj.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    return cpfCnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  };

  const loading = isLoading || isFetching;
  const showHint =
    isOpen &&
    searchTerm.trim().length > 0 &&
    searchTerm.trim().length < 2 &&
    !/^\d+$/.test(searchTerm.trim());

  return (
    <div className="relative" ref={containerRef}>
      <Label htmlFor="cliente-search">
        {label}
        {required ? " *" : ""}
      </Label>
      <div className="relative mt-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          id="cliente-search"
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder="Digite nome, CPF/CNPJ, e-mail ou ID (mín. 2 letras)..."
          className="pl-10"
          autoComplete="off"
        />
      </div>

      {isOpen && showHint && (
        <Card className="absolute z-50 w-full mt-1 border shadow-lg bg-white">
          <div className="p-3 text-sm text-gray-500">Digite pelo menos 2 caracteres para buscar.</div>
        </Card>
      )}

      {isOpen && canQuery && loading && (
        <Card className="absolute z-50 w-full mt-1 border shadow-lg bg-white">
          <div className="p-3 text-sm text-gray-500">Buscando clientes...</div>
        </Card>
      )}

      {isOpen && canQuery && !loading && clientes.length > 0 && (
        <Card className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto border shadow-lg bg-white">
          <div className="p-2">
            {clientes.map((cliente) => (
              <div
                key={cliente.id}
                onClick={() => handleClienteSelect(cliente)}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer rounded-md border-b last:border-b-0"
              >
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center shrink-0">
                  <User className="h-4 w-4 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded border">
                      {cliente.id}
                    </span>
                    <span className="font-medium text-gray-900 truncate">{cliente.nome}</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1 flex flex-wrap gap-2">
                    {cliente.cpf_cnpj && (
                      <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">
                        {formatCpfCnpj(cliente.cpf_cnpj)}
                      </span>
                    )}
                    {cliente.cidade && cliente.estado && (
                      <span className="text-gray-500 text-xs">
                        {cliente.estado} / {cliente.cidade}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {isOpen && canQuery && !loading && clientes.length === 0 && (
        <Card className="absolute z-50 w-full mt-1 border shadow-lg bg-white">
          <div className="p-4 text-center text-gray-500 text-sm">Nenhum cliente encontrado</div>
        </Card>
      )}
    </div>
  );
}
